import {LeavePeriod, Organisation, Deployment, LeavePeriodType, DeploymentState} from '@cquential/api';
import {PlanbordLeavePeriod, ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
import DeploymentStateMapping from '@/util/deploymentstatemapping';
import Dates from '@cquential/date-utils';

export default class LeavePeriodHandler {
    public static addProblematicDays(period: LeavePeriod, resource: ResourceWithKeyedByDateObjects, organisation: Organisation): PlanbordLeavePeriod {
        const extendedPeriod: PlanbordLeavePeriod = {
            ...period,
            problematicDays: [],
        };

        if (resource.deployments) {
            LeavePeriodHandler.markDaysWithoutDeployments(extendedPeriod, resource.deployments);

            if (period.type !== LeavePeriodType.Manual) {
                LeavePeriodHandler.markDeploymentsAndSpecificationsNotSetToLeave(extendedPeriod, resource.deployments, organisation);
            }

            if (period.type === LeavePeriodType.HoursSpreadOverWeekDays || period.type === LeavePeriodType.HoursSpreadOverFullWeeks) {
                LeavePeriodHandler.markUnequalSpreadOfHours(extendedPeriod, resource.deployments, organisation);
            }

            LeavePeriodHandler.removeDuplicateDays(extendedPeriod);
        }

        return extendedPeriod;
    }

    private static removeDuplicateDays(period: PlanbordLeavePeriod): void {
        const result: Record<string, Date> = period.problematicDays.reduce((accumulator: Record<string, Date>, current: Date): Record<string, Date> => {
            accumulator[current.toISOString()] = current;

            return accumulator;
        }, {});

        period.problematicDays = Object.values(result);
    }

    private static markDaysWithoutDeployments(period: PlanbordLeavePeriod, deployments: Record<string, Deployment[]>): void {
        for (let c: Date = new Date(period.beginDate), e: Date = new Date(period.endDate); c <= e; c = Dates.Utils.addDays(c, 1)) {
            const date: string = Dates.Formatter.formatDateISO(c);
            if (date in deployments && deployments[date].length > 0) {
                period.problematicDays.push(c);
            }
        }
    }

    private static markDeploymentsAndSpecificationsNotSetToLeave(period: PlanbordLeavePeriod, deployments: Record<string, Deployment[]>, organisation: Organisation): void {
        const expectedStateId: number = DeploymentStateMapping.getStateMapping(organisation)[DeploymentState.Free];
        for (let c: Date = new Date(period.beginDate), e: Date = new Date(period.endDate); c <= e; c = Dates.Utils.addDays(c, 1)) {
            const date: string = Dates.Formatter.formatDateISO(c);
            this.markDeploymentsAndSpecificationsNotSetToLeaveOnDay(period, deployments[date] || [], expectedStateId);
        }
    }

    private static markDeploymentsAndSpecificationsNotSetToLeaveOnDay(period: PlanbordLeavePeriod, deployments: Deployment[], expectedStateId: number): void {
        for (const deployment of deployments) {
            if (deployment.stateId !== expectedStateId) {
                period.problematicDays.push(new Date(deployment.date));

                return;
            }

            if (deployment.specifications) {
                for (const specification of deployment.specifications) {
                    if (specification.stateId !== expectedStateId) {
                        period.problematicDays.push(new Date(deployment.date));

                        return;
                    }
                }
            }
        }
    }

    private static markUnequalSpreadOfHours(period: PlanbordLeavePeriod, deployments: Record<string, Deployment[]>, organisation: Organisation): void {
        const expectedStateId: number = DeploymentStateMapping.getStateMapping(organisation)[DeploymentState.Free];

        for (const timestamp in period.expectedSpread) {
            const unixStamp: number = Number(timestamp);
            const date: Date = new Date(unixStamp * 1000);
            const hoursForDay: number = this.countHoursForDeployments(deployments[Dates.Formatter.formatDateISO(date)] || [], expectedStateId);

            if (hoursForDay !== period.expectedSpread[unixStamp]) {
                period.problematicDays.push(date);
            }
        }
    }

    private static countHoursForDeployments(deployments: Deployment[], expectedStateId: number): number {
        let result: number = 0;
        for (const deployment of deployments) {
            if (deployment.specifications) {
                for (const specification of deployment.specifications) {
                    if (specification.stateId === expectedStateId) {
                        result += Dates.Utils.diffInSeconds(specification.end, specification.begin);
                    }
                }
            }
        }

        return result;
    }
}
